import ExpandMore from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import { t } from 'i18next';

import StatusChip from '~pages/Settings/components/StatusChip';
import {
  CatalogedCompanySettingKey,
  companySettingsCatalog,
} from '~pages/Settings/Configuration/companySettingsCatalog';
import useVisibleCompanySettingKeys from '~pages/Settings/Configuration/useVisibleCompanySettingKeys';
import { rutterManageSettingsLink } from '~router/routePaths';
import type EN_translation from '~translations/en/translation.json';

import {
  OnboardingAccordion,
  OnboardingExplainer,
  SettingsPageLink,
  StepCallout,
  TitleText,
} from '../CompanyOnboardingPrimitives';

type Recommendation = 'default' | 'off' | 'on';

const COMPANY_DEFAULT_SETTINGS = [
  {
    labelKey: 'administration.configuration.settings.auto_order_completion.setting_title',
    recommendation: 'off',
    settingKey: 'auto_complete_order',
  },
  {
    labelKey: 'administration.configuration.settings.geofence_creation.setting_title',
    recommendation: 'on',
    settingKey: 'site_radius',
  },
  {
    labelKey: 'administration.configuration.settings.geofence_timer.setting_title',
    recommendation: 'default',
    settingKey: 'default_geofence_delay_seconds',
  },
  {
    labelKey: 'administration.configuration.settings.date_format.setting_title',
    recommendation: 'default',
    settingKey: 'date_format',
  },
  {
    labelKey: 'administration.configuration.settings.onsite_defaults.setting_title',
    recommendation: 'default',
    settingKey: 'default_onsite_minutes',
  },
  {
    labelKey: 'administration.configuration.settings.allowed_units.setting_title',
    recommendation: 'default',
    settingKey: 'allowed_order_units_of_measure',
  },
  {
    labelKey: 'administration.configuration.settings.default_project_terms.setting_title',
    recommendation: 'default',
    settingKey: 'default_project_terms',
  },
  {
    labelKey: 'administration.configuration.settings.default_payment_term.setting_title',
    recommendation: 'default',
    settingKey: 'default_payment_term_id',
  },
] as const;

type CompanyDefaultSettingKey = (typeof COMPANY_DEFAULT_SETTINGS)[number]['settingKey'];

interface ConfigurationSetting {
  labelKey?: string;
  recommendation: Recommendation;
  settingKey: CompanyDefaultSettingKey | CatalogedCompanySettingKey;
}

const CONFIGURATION_GROUPS: {
  groupKey: keyof typeof EN_translation.company_onboarding.modules.configurations.work_area.groups;
  link?: string;
  settings: readonly ConfigurationSetting[];
}[] = [
  { groupKey: 'company_defaults', settings: COMPANY_DEFAULT_SETTINGS },
  {
    groupKey: 'dispatch_table_columns',
    settings: [
      { recommendation: 'on', settingKey: 'hide_dispatch_orders_equipment_type_column' },
      { recommendation: 'on', settingKey: 'hide_dispatch_orders_materials_column' },
      { recommendation: 'on', settingKey: 'hide_dispatch_orders_pickup_dropoff_column' },
      { recommendation: 'on', settingKey: 'hide_dispatch_orders_start_time_column' },
      { recommendation: 'off', settingKey: 'show_dispatch_jobs_materials_column' },
      { recommendation: 'off', settingKey: 'show_dispatch_jobs_pickup_dropoff_column' },
      {
        recommendation: 'off',
        settingKey: 'show_dispatch_jobs_truck_and_trailer_column',
      },
    ],
  },
  {
    groupKey: 'dispatch',
    settings: [
      {
        labelKey: companySettingsCatalog.sort_dispatch_orders_by_dispatch_number.labelKey,
        recommendation: 'off',
        settingKey: 'sort_dispatch_orders_by_dispatch_number',
      },
      {
        recommendation: 'on',
        settingKey: 'hide_driver_equipment_when_job_has_equipment',
      },
      {
        labelKey:
          companySettingsCatalog.collapse_dispatch_order_cards_by_default.labelKey,
        recommendation: 'off',
        settingKey: 'collapse_dispatch_order_cards_by_default',
      },
    ],
  },
  {
    groupKey: 'forms_and_filters',
    settings: [
      { recommendation: 'off', settingKey: 'show_default_digital_ticket_view' },
      { recommendation: 'off', settingKey: 'show_contract_number' },
      { recommendation: 'off', settingKey: 'show_service_class' },
    ],
  },
  {
    groupKey: 'order_defaults',
    settings: [
      {
        labelKey: companySettingsCatalog.chase_missing_tickets_default_on.labelKey,
        recommendation: 'off',
        settingKey: 'chase_missing_tickets_default_on',
      },
    ],
  },
  {
    groupKey: 'accounting_export',
    link: rutterManageSettingsLink,
    settings: [
      {
        recommendation: 'off',
        settingKey: 'accounting_platform_assigns_document_number',
      },
      {
        recommendation: 'off',
        settingKey: 'accounting_export_populates_invoice_service_date',
      },
    ],
  },
];

const COPY_PREFIX = 'company_onboarding.modules.configurations.work_area';

const SettingRow = ({ labelKey, recommendation, settingKey }: ConfigurationSetting) => (
  <Box
    data-test-id={`company-onboarding-configuration-${settingKey}`}
    sx={{
      alignItems: 'flex-start',
      borderTop: 1,
      borderColor: 'divider',
      display: 'flex',
      gap: 2,
      py: 1.5,
    }}
  >
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <OnboardingExplainer
        copyKey={`${COPY_PREFIX}.settings.${settingKey}`}
        titleKey={labelKey ?? `${COPY_PREFIX}.settings.${settingKey}.label`}
      />
    </Box>
    <StatusChip
      isSuccess={recommendation === 'on'}
      label={t(`${COPY_PREFIX}.recommendation.${recommendation}`)}
      size="small"
      sx={{ flexShrink: 0 }}
    />
  </Box>
);

interface ConfigurationsStepProps {
  settingsLink: string;
}

const ConfigurationsStep = ({ settingsLink }: ConfigurationsStepProps) => {
  const visibleCatalogKeys = useVisibleCompanySettingKeys();
  const isSettingVisible = (settingKey: ConfigurationSetting['settingKey']) => {
    const isCatalogSetting = Object.hasOwn(companySettingsCatalog, settingKey);
    const isCatalogSettingEnabled = visibleCatalogKeys.some((key) => key === settingKey);

    return !isCatalogSetting || isCatalogSettingEnabled;
  };

  return (
    <Box data-test-id="company-onboarding-configurations">
      <StepCallout
        body={t(`${COPY_PREFIX}.callout_body`)}
        title={t(`${COPY_PREFIX}.callout_title`)}
      >
        <SettingsPageLink
          data-test-id="company-onboarding-configurations-open"
          settingsLink={settingsLink}
        >
          {t(`${COPY_PREFIX}.open_configuration`)}
        </SettingsPageLink>
      </StepCallout>
      {CONFIGURATION_GROUPS.map(({ groupKey, link, settings }) => {
        const visibleSettings = settings.filter(({ settingKey }) =>
          isSettingVisible(settingKey),
        );

        if (!visibleSettings.length) {
          return null;
        }

        return (
          <OnboardingAccordion
            data-test-id={`company-onboarding-configuration-group-${groupKey}`}
            key={groupKey}
            sx={(theme) => ({
              border: `1px solid ${theme.semantic.borderStrong}`,
              borderRadius: 2,
              mt: 1.5,
            })}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <TitleText variant="h6">{t(`${COPY_PREFIX}.groups.${groupKey}`)}</TitleText>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {visibleSettings.map((setting) => (
                <SettingRow key={setting.settingKey} {...setting} />
              ))}
              {link ? (
                <SettingsPageLink
                  data-test-id={`company-onboarding-configuration-group-${groupKey}-open`}
                  settingsLink={link}
                >
                  {t(`${COPY_PREFIX}.groups_open.${groupKey}`)}
                </SettingsPageLink>
              ) : null}
            </AccordionDetails>
          </OnboardingAccordion>
        );
      })}
    </Box>
  );
};

export default ConfigurationsStep;
